import { Trash2 } from 'lucide-react';
import { apiCall } from './useAutoSave';
import StepPostup from './StepPostup';
import StepIngredience from './StepIngredience';
import StepMedia from './StepMedia';
import StepKalkulaceTab from './StepKalkulaceTab';

const ACCENT = '#EF9F27';
const ACCENT_TEXT = '#412402';

// Řízený rodičem (draft buffer): pole čte z `step` (sloučený krok), změny hlásí
// přes `onPatchStep(patch)`. Neukládá sám — uloží se přes tlačítko Uložit.
export default function StepDetail({ step, stepIndex, stepBom, allBomItems, setBomItems, activeTab, setActiveTab, isDark, stroje, jednotky, ingredienty, recept, krokCost, jednotkaZkratka, receptTotal, itemCosts, materialTotal, costsPending, davkaMnozstvi, onNavigate, onPatchStep, onAddBom, onUpdateBom, onDeleteBom, onReorderBom, onReload, onCostChanged }) {
    const deleteStep = async () => {
        if (!confirm('Smazat krok? Ingredience budou presunute do Neprirazeno.')) return;
        try {
            await apiCall(`/api/recepty/sop/kroky/${step.id}`, 'DELETE');
            setBomItems(prev => prev.map(b => b.sop_krok_id === step.id ? { ...b, sop_krok_id: null } : b));
            onReload();
        } catch {
            window.dispatchEvent(new CustomEvent('in-app-toast', {
                detail: { id: crypto.randomUUID(), title: 'Chyba', body: 'Nepodarilo se smazat krok.' },
            }));
        }
    };

    const secondaryColor = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-secondary)';
    const mutedColor = isDark ? 'rgba(255,255,255,0.3)' : 'var(--text-muted)';
    const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';

    const mediaCount = (step.media || []).length;
    const noteCount = (step.poznamky || []).length;
    const ingCount = stepBom.length;

    const tabs = [
        { id: 'postup', label: 'Postup', count: noteCount },
        { id: 'ingredience', label: 'Ingredience', count: ingCount },
        { id: 'media', label: 'Media', count: mediaCount },
        { id: 'kalkulace', label: 'Kalkulace', count: 0 },
    ];

    return (
        <div className="rounded-xl" style={{
            background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)',
            border: `1px solid ${borderColor}`,
        }}>
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: `1px solid ${borderColor}` }}>
                {/* Step number circle */}
                <div className="flex items-center justify-center rounded-full text-xs font-bold"
                    style={{ width: 28, height: 28, flexShrink: 0, background: ACCENT, color: ACCENT_TEXT }}>
                    {stepIndex + 1}
                </div>

                {/* Inline editable name (řízené draftem) */}
                <input
                    value={step.nazev || ''}
                    onChange={e => onPatchStep({ nazev: e.target.value })}
                    className="flex-1 min-w-0"
                    style={{
                        background: 'transparent', border: 'none', outline: 'none',
                        fontSize: 15, fontWeight: 500, color: 'var(--text-primary)',
                        padding: '2px 0',
                    }}
                    placeholder="Nazev kroku..."
                />

                {/* Delete button */}
                <button onClick={deleteStep}
                    className="flex items-center gap-1 text-xs font-medium"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: '4px 8px', borderRadius: 5, flexShrink: 0 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <Trash2 size={12} /> Smazat krok
                </button>
            </div>

            {/* Tab bar */}
            <div className="flex items-center gap-0 px-4" style={{ borderBottom: `1px solid ${borderColor}` }}>
                {tabs.map(tab => (
                    <button key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className="text-xs font-medium py-2.5 px-3"
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            borderBottom: `2px solid ${activeTab === tab.id ? ACCENT : 'transparent'}`,
                            color: activeTab === tab.id ? ACCENT : secondaryColor,
                            marginBottom: -1,
                        }}>
                        {tab.label}{tab.count > 0 ? ` (${tab.count})` : ''}
                    </button>
                ))}
            </div>

            {/* Tab content */}
            <div className="p-4">
                {activeTab === 'postup' && (
                    <StepPostup value={step} isDark={isDark} stroje={stroje} ingredienty={ingredienty} recept={recept} onPatch={onPatchStep} onReload={onReload} />
                )}
                {activeTab === 'ingredience' && (
                    <StepIngredience
                        step={step}
                        stepBom={stepBom}
                        isDark={isDark}
                        jednotky={jednotky}
                        itemCosts={itemCosts}
                        materialTotal={materialTotal}
                        costsPending={costsPending}
                        jednotkaZkratka={jednotkaZkratka}
                        davkaMnozstvi={davkaMnozstvi}
                        onNavigate={onNavigate}
                        onAddBom={onAddBom}
                        onUpdateBom={onUpdateBom}
                        onDeleteBom={onDeleteBom}
                        onReorderBom={onReorderBom}
                    />
                )}
                {activeTab === 'media' && (
                    <StepMedia step={step} isDark={isDark} onReload={onReload} />
                )}
                {activeTab === 'kalkulace' && (
                    <StepKalkulaceTab krokCost={krokCost} jednotkaZkratka={jednotkaZkratka} receptTotal={receptTotal} isDark={isDark} />
                )}
            </div>
        </div>
    );
}
